// styleTokens.js
//
// Design tokens + composed styles + variant helpers for Ferrum IQ pages.
// v3.5.93.0 introduces this file as the "quick option" anchor — captures
// the de-facto design system that has existed across the seven main pages
// (QuotePage / VoiceQuotePage / BundleQuotePage / MultiSiteQuotePage /
// FlexITQuotePage / QuotesPage / ProposalsPage) as inline style values.
// Same hex codes, same pixel scale, same patterns — just centralized so
// new pages can import instead of squinting at existing ones.
//
// THREE LAYERS:
//   1. Raw tokens      — colors / typography / spacing / radius / etc.
//                        Use directly: `style={{ color: colors.muted }}`.
//   2. Composed styles — pre-baked style objects for the most common
//                        patterns: table cells, KPI rows, cards, inputs,
//                        buttons. Use directly: `style={styles.tableCell}`,
//                        or spread for overrides: `style={{ ...styles.tableCell, color: 'red' }}`.
//   3. Variant helpers — functions that return styles parameterized by
//                        type: `statusPill('approved')`, `typeBadge('flexit')`.
//                        Use directly: `style={statusPill('viewed')}`.
//
// MIGRATION NOTE: existing pages stay inline — no obligation to migrate.
// New pages SHOULD import from here. Pages touched for unrelated reasons
// MAY migrate opportunistically (a single style block at a time; never as
// a "rewrite the whole page" effort). The medium-option pass (slotted in
// §10e backlog) extracts the most-used composed styles into actual React
// primitive components — at that point pages migrate to components, not
// raw style imports.
//
// PRE-EXISTING HELPERS — already centralized elsewhere, DO NOT duplicate:
//   • gmColor(gm) / gmBg(gm)              — from src/lib/pricing.js
//   • fmt$(n) / fmt$0(n) / fmtPct(n)      — from src/lib/pricing.js
// New pages should import those from pricing.js, not re-derive.

// ─── 1. RAW TOKENS ─────────────────────────────────────────────────────────

// Color palette. Every hex appearing in 2+ pages is captured here.
// Single-page-only colors (e.g. a unique chart fill) stay inline.
export const colors = {
  // ── Brand / primary ──
  primary:        '#0f1e3c',   // Headings, primary text, dark UI surfaces (sidebar, KPI strip)
  accent:         '#1e40af',   // Quote IDs (monospace), links, primary action accent
  accentBg:       '#dbeafe',   // Light accent surface (button backgrounds, info pills)
  accentBorder:   '#bfdbfe',   // Light accent border (paired with accentBg)

  // ── Body text + neutrals ──
  body:           '#374151',   // Default body text in tables, cards
  bodyStrong:     '#1f2937',   // Slightly stronger than body (rare)
  muted:          '#6b7280',   // Labels, secondary text, table header labels
  mutedSoft:      '#9ca3af',   // Placeholder text, em-dash separators
  faint:          '#d1d5db',   // Disabled, very-low-emphasis dividers

  // ── Surfaces ──
  white:          '#ffffff',   // Card / table / input default background
  surfaceAlt:     '#fafafa',   // Alternating table rows (every other one)
  surfaceMuted:   '#f9fafb',   // Table headers, hover targets
  surfaceTint:    '#f3f4f6',   // Generic light grey for hover or disabled bg
  surfaceHover:   '#f0f7ff',   // Table row hover (subtle blue tint)

  // ── Borders ──
  border:         '#e5e7eb',   // Default border (cards, tables, dividers)
  borderInput:    '#d1d5db',   // Input borders (slightly darker for affordance)
  borderDark:     '#1e3a5f',   // Dark-surface dividers (inside KPI strip / Deal Summary)

  // ── Semantic — status pill palette (always paired bg/text) ──
  // Use via statusPill() helper rather than referencing these directly
  // unless you need a custom variant.
  status: {
    // amber — warning / draft-viewed / pending
    amberBg:    '#fef3c7',
    amberText:  '#92400e',
    // green — success / approved / won
    greenBg:    '#dcfce7',
    greenText:  '#166534',
    // red — declined / error / lost
    redBg:      '#fee2e2',
    redText:    '#991b1b',
    // blue — sent for review / info / in-progress
    blueBg:     '#dbeafe',
    blueText:   '#1e40af',
    // gray — draft / superseded / neutral
    grayBg:     '#f3f4f6',
    grayText:   '#6b7280',
  },

  // ── Quote-type badge palette (used in QuotesPage list view) ──
  // Use via typeBadge() helper.
  quoteType: {
    voice:     '#7c3aed',    // standalone Voice
    multisite: '#6d28d9',    // Multi-Site
    flexit:    '#f97316',    // FlexIT On-Demand
    bundle:    '#4f46e5',    // Bundle (IT+Voice) — v3.7.x: flattened from a blue→violet gradient to a solid indigo per design-system-binding §4 (no gradients)
    // managed_it has no badge (it's the default — no marker)
  },

  // ── Pricing-floor + GM badges ──
  // GM badges use gmColor() / gmBg() from src/lib/pricing.js — already
  // centralized. Do NOT duplicate those palettes here.
};

// Typography. Default font stack is set in src/index.css (CRA base); these
// tokens cover the variations that appear inline across pages.
export const typography = {
  // ── Font families ──
  // Default = inherits from index.css (system-font stack). No need to
  // override on most elements; just don't set fontFamily and you get the
  // default. The mono stack below is for numeric values and IDs.
  mono:           'DM Mono, monospace',

  // ── Sizes (px) ──
  // Conservative scale matching what's actually used across pages. Reps
  // see most app content at 11-12px (table cells / form inputs); 9-10px
  // is for compact metadata (pill text, monospace IDs); 14px is for the
  // few section headers.
  size: {
    tiny:    9,   // Quote ID monospace, micro-meta
    micro:  10,   // Status pill text, type badge text
    small:  11,   // Table cell text, secondary labels
    body:   12,   // Default body, input text, button text
    bodyLg: 13,   // Slightly larger body (uncommon)
    header: 14,   // Section headers (h2-equivalent), card titles
    // Anything larger is page-context-specific and should stay inline.
  },

  // ── Weights ──
  weight: {
    normal:    400,
    medium:    500,
    semibold:  600,   // Table headers, form labels, KPI labels
    bold:      700,   // Status pills, type badges, KPI numeric values
  },

  // ── Letter spacing ──
  letterSpacing: {
    // 0.04em on uppercase table headers — matches the "UPPERCASE COLUMN"
    // pattern across QuotesPage / ActivityPage / ProposalsPage table heads.
    tableHeader: '0.04em',
  },
};

// Spacing scale. Matches the de-facto px values used across pages —
// 2 / 3 / 4 / 5 / 6 / 8 / 10 / 12 / 16 / 20 / 24. No 7px or 11px; if you
// reach for one, you're probably wrong (use the next adjacent value).
export const spacing = {
  xs:  2,
  s:   4,
  m:   6,
  md:  8,
  lg:  10,
  xl:  12,
  xxl: 16,
  xxxl: 20,
  huge: 24,

  // Common composite pad values (use directly with `padding:`)
  cellPad:        '8px 10px',   // Table cells, list rows
  inputPad:       '6px 10px',   // Form inputs, select
  buttonPad:      '6px 10px',   // Default button padding
  buttonPadSmall: '3px 8px',    // Compact buttons (copy URL, action pills)
  cardPad:        16,            // Card padding
  cardPadLarge:   20,            // Roomier cards (KPI strip, modals)
};

// Border radius scale. Three values cover every UI surface in the app.
export const radius = {
  pill:  3,   // Status pills, type badges, small buttons
  input: 5,   // Form inputs, default buttons
  card:  8,   // Cards, modals, tables
};

// Shadows — used sparingly. Most cards use a thin border instead.
export const shadow = {
  card: '0 1px 2px rgba(0, 0, 0, 0.05)',   // Subtle card elevation (rare)
  modal: '0 4px 12px rgba(0, 0, 0, 0.10)', // Modal/dropdown overlay
};

// ─── 2. COMPOSED STYLES ────────────────────────────────────────────────────

// Pre-baked style objects for the most-recurring patterns. Spread these
// into your style prop, or override individual properties:
//   style={styles.tableCell}
//   style={{ ...styles.tableCell, color: colors.accent }}

export const styles = {
  // ── Table cells ──
  tableHeader: {
    padding: spacing.cellPad,
    textAlign: 'left',
    color: colors.muted,
    fontWeight: typography.weight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typography.size.small,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.tableHeader,
    whiteSpace: 'nowrap',
  },
  tableHeaderRow: {
    background: colors.surfaceMuted,
  },
  tableCell: {
    padding: spacing.cellPad,
    color: colors.body,
    fontSize: typography.size.body,
  },
  // Use for the first cell in a row (typically the client/entity name)
  tableCellPrimary: {
    padding: spacing.cellPad,
    fontWeight: typography.weight.semibold,
    color: colors.primary,
  },
  // Monospace numeric cell (quote IDs, dollar amounts, percentages)
  tableCellMono: {
    padding: spacing.cellPad,
    fontFamily: typography.mono,
    fontSize: typography.size.small,
    color: colors.accent,
    fontWeight: typography.weight.semibold,
  },
  // Subdued meta cell (dates, secondary attribution)
  tableCellMeta: {
    padding: spacing.cellPad,
    color: colors.muted,
    fontSize: typography.size.small,
  },

  // ── Cards ──
  card: {
    background: colors.white,
    borderRadius: radius.card,
    border: `1px solid ${colors.border}`,
    overflow: 'hidden',
  },
  cardSection: {
    padding: spacing.cardPad,
  },

  // ── Form ──
  // Field label that sits above an input
  fieldLabel: {
    fontSize: typography.size.small,
    fontWeight: typography.weight.semibold,
    color: colors.muted,
    marginBottom: spacing.s,
    display: 'block',
  },
  input: {
    padding: spacing.inputPad,
    border: `1px solid ${colors.borderInput}`,
    borderRadius: radius.input,
    fontSize: typography.size.body,
    outline: 'none',
    background: colors.white,
    color: colors.body,
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    padding: spacing.inputPad,
    border: `1px solid ${colors.borderInput}`,
    borderRadius: radius.input,
    fontSize: typography.size.body,
    background: colors.white,
    color: colors.body,
  },

  // ── Buttons ──
  // Primary action — solid accent
  buttonPrimary: {
    padding: spacing.buttonPad,
    fontSize: typography.size.body,
    fontWeight: typography.weight.semibold,
    color: colors.white,
    background: colors.accent,
    border: 'none',
    borderRadius: radius.input,
    cursor: 'pointer',
  },
  // Secondary action — light accent bg + accent text
  buttonSecondary: {
    padding: spacing.buttonPadSmall,
    fontSize: typography.size.micro,
    fontWeight: typography.weight.semibold,
    color: colors.accent,
    background: colors.accentBg,
    border: `1px solid ${colors.accentBorder}`,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  // Tertiary / ghost — white bg + dark text + thin border
  buttonGhost: {
    padding: spacing.buttonPadSmall,
    fontSize: typography.size.micro,
    fontWeight: typography.weight.semibold,
    color: colors.primary,
    background: colors.white,
    border: `1px solid ${colors.borderInput}`,
    borderRadius: radius.pill,
    cursor: 'pointer',
    textDecoration: 'none',  // For <a> styled as button
  },

  // ── Section headers ──
  h2: {
    fontSize: typography.size.header,
    fontWeight: typography.weight.bold,
    color: colors.primary,
    margin: 0,
  },
  h2Subtitle: {
    fontSize: typography.size.small,
    color: colors.muted,
    marginTop: spacing.xs,
  },

  // ── Page-level layout ──
  // For the outermost page wrapper inside Layout — most pages use this.
  pageRoot: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  pageScroll: {
    flex: 1,
    overflow: 'auto',
    padding: spacing.xxl,
  },
};

// ─── 3. VARIANT HELPERS ────────────────────────────────────────────────────

// Status pill style for a given variant. Returns a style object —
// drop into a <span style={statusPill('approved')}>label</span>.
// Variants: 'draft' | 'sent' | 'viewed' | 'approved' | 'declined' |
//           'expired' | 'superseded' | 'amber' | 'green' | 'red' | 'blue' | 'gray'
// Generic color names are also accepted for ad-hoc usage.
export function statusPill(variant) {
  const map = {
    // Quote/proposal status names mapped to color variants
    draft:           ['grayBg', 'grayText'],
    sent_for_review: ['blueBg', 'blueText'],
    sent:            ['blueBg', 'blueText'],
    in_review:       ['blueBg', 'blueText'],
    viewed:          ['amberBg', 'amberText'],
    approved:        ['greenBg', 'greenText'],
    won:             ['greenBg', 'greenText'],
    declined:        ['redBg', 'redText'],
    lost:            ['redBg', 'redText'],
    expired:         ['amberBg', 'amberText'],
    superseded:      ['grayBg', 'grayText'],
    // Generic color variants — for non-status pills
    amber: ['amberBg', 'amberText'],
    green: ['greenBg', 'greenText'],
    red:   ['redBg', 'redText'],
    blue:  ['blueBg', 'blueText'],
    gray:  ['grayBg', 'grayText'],
  };
  const [bgKey, textKey] = map[variant] || map.gray;
  return {
    display: 'inline-block',
    padding: '2px 8px',           // Slightly wider than other padding tokens
    borderRadius: radius.pill,
    fontSize: typography.size.micro,
    fontWeight: typography.weight.bold,
    background: colors.status[bgKey],
    color: colors.status[textKey],
  };
}

// Quote-type badge style. Returns the style object for the small colored
// pill that sits next to a quote's client name in QuotesPage.
// quoteType: 'voice' | 'bundle' | 'multisite' | 'flexit' (managed_it has no badge).
export function typeBadge(quoteType) {
  const bg = colors.quoteType[quoteType];
  if (!bg) return null;  // managed_it / unknown → caller decides not to render
  return {
    display: 'inline-block',
    marginLeft: spacing.m,
    padding: '1px 5px',
    fontSize: typography.size.tiny,
    fontWeight: typography.weight.bold,
    background: bg,
    color: colors.white,
    borderRadius: radius.pill,
  };
}

// Convenience: get the canonical label that pairs with each typeBadge color.
// Pages typically render the badge + label inline, e.g.:
//   <span style={typeBadge('flexit')}>{typeBadgeLabel('flexit')}</span>
export function typeBadgeLabel(quoteType) {
  return {
    voice:     'Voice',
    multisite: 'Multi-Site',
    flexit:    'FlexIT',
    bundle:    'Bundle',
  }[quoteType] || null;
}

// ─── 4. V3 TOKENS — Unified UX refactor (v3.6.x) ────────────────────────────
// Canonical for all refactored pages. v2 tokens above remain for the
// proposal renderer + back-compat. Migration is per-page; never a rewrite.
// Pure-additive: nothing above this line changes, so this commit produces
// ZERO visual change anywhere — it is preparation for the section-workspace
// refactor (see ferrumiq-refactor-handoff/shared-shell-and-components.md §1).
export const v3 = {
  color: {
    navy: '#0F1E3C', ink: '#1E40AF', inkSoft: '#3057D8',
    inkOnDark: '#86A8FF', inkBg: '#EEF2FB', inkBorder: '#D6E0F5',
    bg: '#FAFBFC', card: '#FFFFFF', cardAlt: '#F6F7F9',
    border: '#EAECEF', borderSoft: '#F0F1F4', divider: '#E7E9EC',
    body: '#1F2937', bodySoft: '#374151', muted: '#6B7280',
    mutedSoft: '#9CA3AF', faint: '#D1D5DB',
    status: {
      amberBg:'#FEF3C7', amberText:'#92400E', greenBg:'#DCFCE7', greenText:'#166534',
      redBg:'#FEE2E2', redText:'#991B1B', blueBg:'#DBEAFE', blueText:'#1E40AF',
      grayBg:'#F3F4F6', grayText:'#6B7280',
    },
    flexitOrange:'#F97316', flexitOrangeBg:'#FFF7ED', flexitOrangeBorder:'#FED7AA',
    moneyPositive:'#34D399', // on the navy card only
  },
  font: {
    family: "'DM Sans', system-ui, sans-serif",
    mono:   "'DM Mono', ui-monospace, monospace",
    size: { micro:11, small:12, body:14, label:13, header:17, pageMeta:13,
            pageTitle:22, section:26, money:30, moneyXL:36 },
    weight: { normal:400, medium:500, semibold:600, bold:700 },
    letterSpacing: { uppercase:'0.04em', tight:'-0.3px', tighter:'-0.5px' },
    lineHeight: { tight:1.1, normal:1.5 },
  },
  space: {
    xs:4, s:6, m:8, md:12, lg:16, xl:20, xxl:24, huge:32,
    cellPad:'12px 16px', inputPad:'10px 12px', cardPad:20, sectionGap:24,
  },
  radius: { pill:999, input:8, card:10, cardLarge:12, tile:'22%' },
  shadow: {
    card:'0 1px 2px rgba(0,0,0,0.04)',
    modal:'0 4px 12px rgba(0,0,0,0.10)',
    rail:'0 -4px 24px rgba(15,30,60,0.06)',
  },
  layout: {
    topBarHeight:52, quoteHeaderHeight:80, sectionNavWidth:260, railWidth:320,
    grid:'260px 1fr 320px', paneGap:24, pagePadding:24,
  },
};
